/**
 * Lightweight i18n for the campaign landing page + callback form.
 * No external library. Dictionary + a useT() hook keyed by language.
 *
 * Supported: 'en' (default), 'es'.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export const LANGS = ['en', 'es'];
export const DEFAULT_LANG = 'en';
const STORAGE_KEY = 'hcp_lang';

export const DISCLOSURE_VERSION = '2026-02-phase1-v1';
const LANG_EVENT = 'hcp-lang-change';

// Site header/footer labels shown on /quote pages
const chrome = {
  en: {
    nav_protection_center: 'Protection Center',
    nav_services: 'Services',
    nav_about: 'About Us',
    nav_contact: 'Contact',
    nav_cta: 'Start My Protection Profile',
    en_only_note: '',
    ft_slogan: 'Insured Right. Sleep Tight.',
    ft_desc: 'Independent Property & Casualty Insurance Agency serving Texas families, individuals, and business owners.',
    ft_privacy_note: 'Your information is used only to prepare your insurance quote or coverage review. We do not sell your information.',
    ft_quick_links: 'Quick Links',
    ft_home: 'Home',
    ft_privacy: 'Privacy Policy',
    ft_coverage: 'Coverage Options',
    ft_rights: 'All rights reserved.',
    ft_disc_1: 'Coverage subject to underwriting and approval. Not all products available in all states.',
    ft_disc_2: 'Here Comes Protection is an independent insurance agency.',
    ft_disc_3: 'Quotes are based on information provided and may change upon verification.',
    cov_auto: 'Auto Insurance', cov_home: 'Home Insurance', cov_life: 'Life Insurance',
    cov_business: 'Business Insurance', cov_commercial_auto: 'Commercial Auto', cov_umbrella: 'Umbrella Coverage',
    cov_renters: 'Renters Insurance', cov_medicare: 'Medicare Options',
  },
  es: {
    nav_protection_center: 'Centro de Protección',
    nav_services: 'Servicios',
    nav_about: 'Nosotros',
    nav_contact: 'Contacto',
    nav_cta: 'Iniciar mi perfil de protección',
    en_only_note: '(en inglés)',
    ft_slogan: 'Asegurado bien. Duerma tranquilo.',
    ft_desc: 'Agencia independiente de seguros de propiedad y responsabilidad civil al servicio de familias, personas y dueños de negocios en Texas.',
    ft_privacy_note: 'Su información se usa únicamente para preparar su cotización o revisión de cobertura. No vendemos su información.',
    ft_quick_links: 'Enlaces rápidos',
    ft_home: 'Inicio',
    ft_privacy: 'Política de privacidad',
    ft_coverage: 'Opciones de cobertura',
    ft_rights: 'Todos los derechos reservados.',
    ft_disc_1: 'Cobertura sujeta a suscripción y aprobación. No todos los productos están disponibles en todos los estados.',
    ft_disc_2: 'Here Comes Protection es una agencia de seguros independiente.',
    ft_disc_3: 'Las cotizaciones se basan en la información proporcionada y pueden cambiar tras su verificación.',
    cov_auto: 'Seguro de Auto', cov_home: 'Seguro de Hogar', cov_life: 'Seguro de Vida',
    cov_business: 'Seguro de Negocios', cov_commercial_auto: 'Auto Comercial', cov_umbrella: 'Cobertura Umbrella',
    cov_renters: 'Seguro de Inquilinos', cov_medicare: 'Opciones de Medicare',
  },
};

// Language for shared header/footer: follows the toggle on /quote pages, English elsewhere
export function useChromeT() {
  const { pathname } = useLocation();
  const [lang, setLang] = useState(() => getStoredLang() || DEFAULT_LANG);
  useEffect(() => {
    const onChange = (e) => setLang(e.detail);
    window.addEventListener(LANG_EVENT, onChange);
    return () => window.removeEventListener(LANG_EVENT, onChange);
  }, []);
  const active = pathname.startsWith('/quote') ? lang : DEFAULT_LANG;
  return [active, (key) => chrome[active][key] ?? chrome.en[key]];
}

export const dict = {
  en: {
    // Nav / toggle
    lang_toggle: 'English | Español',
    lang_english: 'English',
    lang_spanish: 'Español',

    // Hero
    hero_eyebrow: 'The Protection Center',
    hero_headline: 'Your Insurance. More Options. Better Possibilities.',
    hero_sub:
      "Why settle for one insurance option? Here Comes Protection helps Texans compare coverage options from multiple insurance carriers to find protection that fits their needs and budget.",
    hero_cta_scroll: 'Get My Free Comparison →',
    hero_cta_callback: 'Request a Callback',

    // Explainer
    how_title: 'How We Compare Coverage',
    how_1_title: 'Tell us what to protect',
    how_1_desc: 'A few quick details — no SSN, no driver license needed to start.',
    how_2_title: 'We compare carrier options',
    how_2_desc: 'Our licensed Texas team reviews options from our carrier network.',
    how_3_title: 'You pick what fits',
    how_3_desc: 'Clear choices. Friendly guidance. No pressure.',

    // Products
    products_title: 'What would you like to protect?',
    products_hint: 'Our current campaign focuses on Auto and Home — all categories below are available.',
    product_auto: 'Auto Insurance',
    product_home: 'Homeowners Insurance',
    product_renters: 'Renters Insurance',
    product_bundle: 'Home + Auto Bundle',
    product_business: 'Business Insurance',
    product_life: 'Life Insurance',
    product_featured_badge: 'Campaign focus',

    // Form
    form_title: 'Start My Free Comparison',
    form_sub: 'Mobile-friendly. Takes about 60 seconds.',
    f_first_name: 'First name',
    f_last_name: 'Last name',
    f_phone: 'Phone number',
    f_email: 'Email address',
    f_zip: 'ZIP code',
    f_product: 'Insurance product',
    f_product_placeholder: 'Choose a product',
    f_current_carrier: 'Current carrier (optional)',
    f_policy_expiration: 'Policy expiration date (optional)',
    f_preferred_contact: 'Preferred contact method',
    f_contact_phone: 'Phone call',
    f_contact_text: 'Text / SMS',
    f_contact_email: 'Email',
    f_best_time: 'Best time to reach you',
    f_notes: 'Anything else we should know? (optional)',
    f_submit: 'Submit My Request',
    f_submitting: 'Submitting...',

    // Consent
    consent_title: 'Consent & Communication',
    consent_required_label:
      'I authorize Here Comes Protection to contact me regarding my insurance inquiry using the contact information I provided.',
    consent_required_help: 'Required — this is how we respond to your quote request.',
    consent_marketing_email_label:
      'I agree to receive promotional emails, insurance updates, and special offers from Here Comes Protection. I understand I can unsubscribe at any time.',
    consent_marketing_sms_label:
      'I expressly consent to receive marketing text messages and calls, including those using automated technology or prerecorded/artificial voice, from Here Comes Protection at the phone number provided. Consent is not required to obtain an insurance quote or purchase insurance. Message and data rates may apply. Message frequency varies. Reply STOP to opt out of marketing texts.',
    consent_optional_help: 'Optional. Declining this will not affect your quote.',
    privacy_link: 'Privacy Policy',

    // Disclaimers
    disclaimer_savings:
      'Example: A family insuring four vehicles saved over $200 per month by comparing coverage options. Savings are based on an individual example. Results vary. Coverage and rates are subject to carrier eligibility, underwriting, and policy terms.',
    life_mention: 'Protecting more than your property. Ask us about life insurance options, too.',

    // Validation / errors
    err_required: 'This field is required.',
    err_consent_required: 'Please confirm the first consent to submit your request.',
    err_phone_invalid: 'Please enter a valid phone number.',
    err_email_invalid: 'Please enter a valid email address.',
    err_zip_invalid: 'Please enter a valid 5-digit ZIP code.',
    err_rate_limit: 'Too many submissions. Please wait a moment and try again.',
    err_generic:
      "We could not submit your request right now. Please call (972) 292-8640 or email Herecomesprotection@gmail.com and we'll help directly.",

    // Confirmation
    thank_title: 'Thank you!',
    thank_body:
      "Thank you for contacting Here Comes Protection! We've received your request. Our team will review your information and follow up with you.",
    thank_back: 'Back to Here Comes Protection',
    thank_callback_cta: 'Request a Callback',

    // Callback page
    callback_title: 'Request a Callback',
    callback_sub:
      'Leave your details and the best time to reach you. If our team is unavailable, please leave your name, phone number, and the best time to reach you. We appreciate the opportunity to assist you.',
    callback_submit: 'Request My Callback',
    callback_thank:
      'Your callback request has been received. A licensed agent will call you at the time you selected. We appreciate the opportunity to assist you.',

    // Footer / contact
    contact_website: 'Website',
    contact_email: 'Email',
    slogan: 'Insured right. Sleep tight.',
  },
  es: {
    lang_toggle: 'English | Español',
    lang_english: 'English',
    lang_spanish: 'Español',

    hero_eyebrow: 'El Centro de Protección',
    hero_headline: 'Su seguro. Más opciones. Mejores posibilidades.',
    hero_sub:
      '¿Por qué conformarse con una sola opción de seguro? Here Comes Protection ayuda a los tejanos a comparar opciones de cobertura de varias compañías de seguros para encontrar la protección que se ajuste a sus necesidades y presupuesto.',
    hero_cta_scroll: 'Obtener mi comparación gratis →',
    hero_cta_callback: 'Solicitar una llamada',

    how_title: 'Así comparamos la cobertura',
    how_1_title: 'Díganos qué desea proteger',
    how_1_desc: 'Unos pocos datos — no necesita Seguro Social ni licencia para comenzar.',
    how_2_title: 'Comparamos opciones de compañías',
    how_2_desc: 'Nuestro equipo con licencia en Texas revisa opciones de nuestra red de aseguradoras.',
    how_3_title: 'Usted elige lo que le conviene',
    how_3_desc: 'Opciones claras. Orientación amistosa. Sin presión.',

    products_title: '¿Qué le gustaría proteger?',
    products_hint: 'Nuestra campaña actual se enfoca en Auto y Hogar — todas las categorías están disponibles.',
    product_auto: 'Seguro de Auto',
    product_home: 'Seguro de Hogar',
    product_renters: 'Seguro de Inquilinos',
    product_bundle: 'Paquete Hogar + Auto',
    product_business: 'Seguro de Negocios',
    product_life: 'Seguro de Vida',
    product_featured_badge: 'Enfoque de campaña',

    form_title: 'Comenzar mi comparación gratis',
    form_sub: 'Diseñado para móvil. Toma unos 60 segundos.',
    f_first_name: 'Nombre',
    f_last_name: 'Apellido',
    f_phone: 'Número de teléfono',
    f_email: 'Correo electrónico',
    f_zip: 'Código postal',
    f_product: 'Producto de seguro',
    f_product_placeholder: 'Elija un producto',
    f_current_carrier: 'Compañía actual (opcional)',
    f_policy_expiration: 'Fecha de vencimiento de la póliza (opcional)',
    f_preferred_contact: 'Método de contacto preferido',
    f_contact_phone: 'Llamada telefónica',
    f_contact_text: 'Mensaje de texto / SMS',
    f_contact_email: 'Correo electrónico',
    f_best_time: 'Mejor hora para contactarle',
    f_notes: '¿Algo más que debamos saber? (opcional)',
    f_submit: 'Enviar mi solicitud',
    f_submitting: 'Enviando...',

    consent_title: 'Consentimiento y comunicación',
    consent_required_label:
      'Autorizo a Here Comes Protection a contactarme con respecto a mi solicitud de seguro usando la información de contacto que proporcioné.',
    consent_required_help: 'Requerido — así es como respondemos a su solicitud de cotización.',
    consent_marketing_email_label:
      'Acepto recibir correos promocionales, actualizaciones de seguros y ofertas especiales de Here Comes Protection. Entiendo que puedo darme de baja en cualquier momento.',
    consent_marketing_sms_label:
      'Doy mi consentimiento expreso para recibir mensajes de texto y llamadas de mercadeo, incluidos los que usan tecnología automatizada o voces pregrabadas/artificiales, de Here Comes Protection al número de teléfono proporcionado. El consentimiento no es requisito para obtener una cotización ni para comprar un seguro. Pueden aplicar tarifas por mensaje y datos. La frecuencia de mensajes varía. Responda STOP para dejar de recibir mensajes de mercadeo.',
    consent_optional_help: 'Opcional. Rechazarlo no afectará su cotización.',
    privacy_link: 'Política de privacidad',

    disclaimer_savings:
      'Ejemplo: Una familia que aseguró cuatro vehículos ahorró más de $200 al mes al comparar opciones de cobertura. Los ahorros se basan en un ejemplo individual. Los resultados varían. La cobertura y las tarifas están sujetas a la elegibilidad de la aseguradora, al proceso de suscripción y a los términos de la póliza.',
    life_mention: 'Protegemos más que sus bienes. Pregúntenos también por nuestras opciones de seguro de vida.',

    err_required: 'Este campo es obligatorio.',
    err_consent_required: 'Por favor confirme el primer consentimiento para enviar su solicitud.',
    err_phone_invalid: 'Por favor ingrese un número de teléfono válido.',
    err_email_invalid: 'Por favor ingrese un correo electrónico válido.',
    err_zip_invalid: 'Por favor ingrese un código postal válido de 5 dígitos.',
    err_rate_limit: 'Demasiadas solicitudes. Por favor espere un momento e inténtelo de nuevo.',
    err_generic:
      'No pudimos enviar su solicitud en este momento. Por favor llame al (972) 292-8640 o escriba a Herecomesprotection@gmail.com y lo ayudaremos directamente.',

    thank_title: '¡Gracias!',
    thank_body:
      '¡Gracias por contactar a Here Comes Protection! Hemos recibido su solicitud. Nuestro equipo revisará su información y se comunicará con usted.',
    thank_back: 'Volver a Here Comes Protection',
    thank_callback_cta: 'Solicitar una llamada',

    callback_title: 'Solicitar una llamada',
    callback_sub:
      'Déjenos sus datos y la mejor hora para contactarle. Si nuestro equipo no está disponible, deje su nombre, su número de teléfono y la mejor hora para comunicarnos. Agradecemos la oportunidad de ayudarle.',
    callback_submit: 'Solicitar mi llamada',
    callback_thank:
      'Hemos recibido su solicitud de llamada. Un agente con licencia le llamará en el horario que seleccionó. Agradecemos la oportunidad de ayudarle.',

    contact_website: 'Sitio web',
    contact_email: 'Correo electrónico',
    slogan: 'Asegurado bien. Duerma tranquilo.',
  },
};

export function getStoredLang() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && LANGS.includes(v)) return v;
  } catch (_) {}
  return null;
}

export function setStoredLang(lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (_) {}
}

/**
 * useT(initialLang?) → [lang, t, setLang]
 * - initialLang can be a URL ?lang= value.
 * - Persists to localStorage.
 */
export function useT(initialLang) {
  const [lang, setLangState] = useState(() => {
    if (initialLang && LANGS.includes(initialLang)) return initialLang;
    return getStoredLang() || DEFAULT_LANG;
  });

  useEffect(() => {
    setStoredLang(lang);
    try {
      document.documentElement.lang = lang;
      window.dispatchEvent(new CustomEvent(LANG_EVENT, { detail: lang }));
    } catch (_) {}
  }, [lang]);

  const t = useCallback(
    (key) => (dict[lang] && dict[lang][key]) || dict[DEFAULT_LANG][key] || key,
    [lang],
  );

  const setLang = useCallback(
    (next) => {
      if (!LANGS.includes(next)) return;
      setLangState(next);
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('lang')) {
          url.searchParams.set('lang', next);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      } catch (_) {}
    },
    [],
  );

  return [lang, t, setLang];
}
